import type { StateCreator } from 'zustand';

import { showToast } from '@/features/layout/notifications';
import type { ProjectScope } from '@/features/sync/project-scope';
import type { AppState } from '@/store/app-state';
import { ACTION_ITEM_FLAG_FIELD } from '@/types/shared';
import { logger } from '@/utils/logger';

import type {
  SpeakerAttribution,
  TranscriptActionAnchor,
  TranscriptSegment,
  TranscriptSession,
} from './types';
import { saveTranscriptSegmentActionItemFlag } from './transcription-api';
import {
  clearPendingTranscriptSegmentActionItemFlagWrites,
  markPendingTranscriptSegmentActionItemFlagWrite,
  mergeTranscriptSegmentsWithPendingActionItemFlags,
} from './transcript-segment-action-item-pending-writes';

export interface TranscriptionSlice {
  transcriptSessions: TranscriptSession[];
  transcriptSegmentsBySession: Record<string, TranscriptSegment[]>;
  transcriptAnchorsBySession: Record<string, TranscriptActionAnchor[]>;
  speakerAttributionsBySession: Record<string, SpeakerAttribution[]>;
  activeTranscriptSessionId: string | null;
  displayedTranscriptSessionIdsBySurface: Record<string, string>;
  transcriptionActions: {
    setTranscriptSessionsFromRemote: (sessions: TranscriptSession[]) => void;
    setTranscriptSegmentsFromRemote: (sessionId: string, segments: TranscriptSegment[]) => void;
    setTranscriptAnchorsFromRemote: (sessionId: string, anchors: TranscriptActionAnchor[]) => void;
    setSpeakerAttributionsFromRemote: (sessionId: string, attributions: SpeakerAttribution[]) => void;
    setActiveTranscriptSessionId: (sessionId: string | null) => void;
    setDisplayedTranscriptSessionId: (surfaceId: string, sessionId: string | null) => void;
    setTranscriptSegmentActionItemFlag: (sessionId: string, segmentId: string, flagged: boolean) => void;
    clearTranscription: () => void;
  };
}

function getTranscriptionProjectScope(state: {
  cloud?: {
    enabled?: boolean;
    orgId?: string | null;
    projectId?: string | null;
  };
}): ProjectScope | null {
  if (!state.cloud?.enabled || !state.cloud.orgId || !state.cloud.projectId) {
    return null;
  }
  return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

function transcriptSegmentWithActionItemFlag(
  segment: TranscriptSegment,
  flagged: boolean,
): TranscriptSegment {
  const next = { ...segment };
  if (flagged) {
    next[ACTION_ITEM_FLAG_FIELD] = true;
  } else {
    delete next[ACTION_ITEM_FLAG_FIELD];
  }
  return next;
}

function persistTranscriptSegmentFlag(scope: ProjectScope, segment: TranscriptSegment): void {
  void saveTranscriptSegmentActionItemFlag(
    scope,
    segment.id,
    segment[ACTION_ITEM_FLAG_FIELD] === true
  ).catch(error => {
    logger.error('[transcription] Failed to save transcript action item flag:', error);
    showToast('Transcript action item flag is still local. Keep this tab open while preservation recovers.', 'error');
  });
}

export const createTranscriptionSlice: StateCreator<
  AppState & TranscriptionSlice,
  [['zustand/immer', never]],
  [],
  TranscriptionSlice
> = (set, get) => ({
  transcriptSessions: [],
  transcriptSegmentsBySession: {},
  transcriptAnchorsBySession: {},
  speakerAttributionsBySession: {},
  activeTranscriptSessionId: null,
  displayedTranscriptSessionIdsBySurface: {},
  transcriptionActions: {
    setTranscriptSessionsFromRemote: (sessions) => set((state) => {
      state.transcriptSessions = sessions;
    }),
    setTranscriptSegmentsFromRemote: (sessionId, segments) => set((state) => {
      const scope = getTranscriptionProjectScope(state);
      const localSegments = state.transcriptSegmentsBySession[sessionId] ?? [];
      state.transcriptSegmentsBySession[sessionId] = scope
        ? mergeTranscriptSegmentsWithPendingActionItemFlags(scope, segments, localSegments)
        : segments;
    }),
    setTranscriptAnchorsFromRemote: (sessionId, anchors) => set((state) => {
      state.transcriptAnchorsBySession[sessionId] = anchors;
    }),
    setSpeakerAttributionsFromRemote: (sessionId, attributions) => set((state) => {
      state.speakerAttributionsBySession[sessionId] = attributions;
    }),
    setActiveTranscriptSessionId: (sessionId) => set((state) => {
      state.activeTranscriptSessionId = sessionId;
    }),
    setDisplayedTranscriptSessionId: (surfaceId, sessionId) => set((state) => {
      if (!sessionId) {
        delete state.displayedTranscriptSessionIdsBySurface[surfaceId];
        return;
      }
      state.displayedTranscriptSessionIdsBySurface[surfaceId] = sessionId;
    }),
    setTranscriptSegmentActionItemFlag: (sessionId, segmentId, flagged) => {
      let segmentToPersist: TranscriptSegment | null = null;

      set((state) => {
        const segments = state.transcriptSegmentsBySession[sessionId];
        if (!segments) {
          return;
        }

        const index = segments.findIndex(segment => segment.id === segmentId);
        const segment = segments[index];
        if (!segment || (segment[ACTION_ITEM_FLAG_FIELD] === true) === flagged) {
          return;
        }

        const nextSegment = transcriptSegmentWithActionItemFlag(segment, flagged);
        segments[index] = nextSegment;
        segmentToPersist = nextSegment;
      });

      if (!segmentToPersist) {
        return;
      }

      const scope = getTranscriptionProjectScope(get());
      if (!scope) {
        return;
      }

      markPendingTranscriptSegmentActionItemFlagWrite(scope, segmentToPersist);
      persistTranscriptSegmentFlag(scope, segmentToPersist);
    },
    clearTranscription: () => set((state) => {
      clearPendingTranscriptSegmentActionItemFlagWrites();
      state.transcriptSessions = [];
      state.transcriptSegmentsBySession = {};
      state.transcriptAnchorsBySession = {};
      state.speakerAttributionsBySession = {};
      state.activeTranscriptSessionId = null;
      state.displayedTranscriptSessionIdsBySurface = {};
    }),
  },
});
